import * as React from 'react';
import { cn } from '@/lib/utils';
import { cva } from 'class-variance-authority';

const SpinWheelWrapper = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-spin-wheel-wrapper', 'relative flex max-w-full flex-col items-center', className)}
    {...props}
  />
));
SpinWheelWrapper.displayName = 'SpinWheelWrapper';

const SpinWheelWrapperCount = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('c-spin-wheel-wrapper-count', 'relative z-0 flex h-12 animate-slide-in-up cursor-default rounded-lg bg-white p-1 px-3 text-base font-bold text-neutral-dark', className)}
    {...props}
  />
));
SpinWheelWrapperCount.displayName = 'SpinWheelWrapperCount';

const SpinWheel = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-spin-wheel', 'relative z-10 -mt-5 aspect-square w-[424px] max-w-full rounded-full bg-white px-3 py-3 shadow-[0px_6px_15px_4.5px_rgba(68,43,140,0.20)]', 'p-[max(12px,1.8vw)]', className)}
    {...props}
  />
));
SpinWheel.displayName = 'SpinWheel';

const SpinWheelSpin = React.forwardRef(({ className, ...props }, ref) => (
  <img
    ref={ref}
    className={cn('c-spin-wheel-spin', 'absolute left-1/2 top-1/2 w-11 -translate-x-1/2 -translate-y-1/2 drop-shadow-[0px_6px_6px_rgba(0,0,0,0.25)]', className)}
    {...props}
  />
));
SpinWheelSpin.displayName = 'SpinWheelSpin';

const SpinWheelSlice = React.forwardRef(({ className, bg, rotate = 0, slices, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-spin-wheel-slice', 'absolute left-1/4 flex h-1/2 w-1/2 origin-bottom items-center justify-center p-6 text-right text-sm font-light text-white', className)}
    style={{ backgroundColor: bg, rotate: `${rotate}deg`, clipPath: slices > 2 ? 'polygon(100% 0 , 50% 100% , 0 0 )' : 'unset' }}
    {...props}
  />
));
SpinWheelSlice.displayName = 'SpinWheelSlice';

const spinWheelRouletteVariants = cva('aspect-square w-[376px] max-w-full rounded-full bg-neutral-100 relative overflow-hidden transition-[transform] ease-in-out', {
  variants: {
    slices: {
      6: '[&>.c-spin-wheel-slice]:w-[58%] [&>.c-spin-wheel-slice]:left-[21%]',
      5: '[&>.c-spin-wheel-slice]:w-[72.5%] [&>.c-spin-wheel-slice]:left-[14%]',
      4: '[&>.c-spin-wheel-slice]:w-full [&>.c-spin-wheel-slice]:left-0',
      3: '[&>.c-spin-wheel-slice]:w-[173%] [&>.c-spin-wheel-slice]:left-[-36.5%]',
      2: '[&>.c-spin-wheel-slice]:w-full [&>.c-spin-wheel-slice]:left-0',
      1: [
        '[&>.c-spin-wheel-slice]:w-full [&>.c-spin-wheel-slice]:h-full [&>.c-spin-wheel-slice]:left-0',
        '[&_.c-spin-wheel-slice-text]:-rotate-0 [&_.c-spin-wheel-slice-text]:max-w-none [&_.c-spin-wheel-slice-text]:w-full',
        '[&_.c-spin-wheel-slice-text]:top-[30%] [&_.c-spin-wheel-slice-text]:left-0 [&_.c-spin-wheel-slice-text]:absolute [&_.c-spin-wheel-slice-text]:text-center'
      ]
    }
  }
});

const SpinWheelRoulette = React.forwardRef(({ className, slices, rotate, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-spin-wheel-roulette', spinWheelRouletteVariants({ slices }), className)}
    style={{ transform: `rotate(-${rotate}deg)`, transitionDuration: '4s' }}
    {...props}
  />
));
SpinWheelRoulette.displayName = 'SpinWheelRoulette';

const SpinWheelSliceText = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('c-spin-wheel-slice-text', 'line-clamp-2 max-w-[120px] -rotate-90 cursor-default leading-tight', className)}
    {...props}
  />
));
SpinWheelSliceText.displayName = 'SpinWheelSliceText';

export { SpinWheelWrapper, SpinWheelWrapperCount, SpinWheel, SpinWheelSlice, SpinWheelRoulette, SpinWheelSliceText, SpinWheelSpin };
